import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Camera, X, Undo2, Redo2, RotateCcw, Check, Crosshair,
  Ruler, Image as ImageIcon, AlertTriangle, MapPin, Plus
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * LiveMeasurementCapture
 * -----------------------
 * Live rear-camera, point-marking room/floor measurement.
 * - Tap directly on the live video to mark wall corners (normalized 0-1 coords).
 * - Real-time overlay: points + connecting edges + running edge lengths + running
 *   perimeter/area.
 * - Calibration: tap two points of a known real-world length and enter that length.
 *   This converts screen units -> real-world units (no vision model needed).
 * - Undo / Redo / Reset operate at the POINT level.
 *
 * onComplete receives:
 *   {
 *     polygonPoints: [{x,y}...]        // normalized, closed shape implied
 *     referenceLinePixels: {x1,y1,x2,y2} // normalized, SAME space as polygon
 *     referenceLength: number,
 *     unit: 'meters' | 'feet',
 *     captureMeta: {...},
 *     thumbnailData: dataURL | null
 *   }
 */
const LiveMeasurementCapture = ({ onComplete, onCancel }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const containerRef = useRef(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const [phase, setPhase] = useState('mark'); // 'mark' | 'calibrate'
  const [unit, setUnit] = useState('meters');

  // Polygon corners (normalized 0-1)
  const [points, setPoints] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Calibration
  const [calPoints, setCalPoints] = useState([]); // up to 2 normalized points
  const [calLength, setCalLength] = useState('');

  const [thumbnail, setThumbnail] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Multi-room session
  const [roomName, setRoomName] = useState('');
  const [savedRooms, setSavedRooms] = useState([]); // committed rooms

  // ---- Camera lifecycle ----
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCameraReady(true);
      setCameraError(null);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(
        err && err.name === 'NotAllowedError'
          ? 'Camera permission denied. Please allow camera access and retry.'
          : 'Unable to access the camera on this device.'
      );
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  // ---- Geometry helpers (normalized units) ----
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  const polygonPerimeter = (pts, closed = true) => {
    if (pts.length < 2) return 0;
    let p = 0;
    for (let i = 0; i < pts.length - 1; i++) p += dist(pts[i], pts[i + 1]);
    if (closed && pts.length >= 3) p += dist(pts[pts.length - 1], pts[0]);
    return p;
  };

  const polygonArea = (pts) => {
    if (pts.length < 3) return 0;
    let area = 0;
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      area += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
    }
    return Math.abs(area) / 2;
  };

  // pixels(normalized) -> real. ratio = realLength / normalizedRefDistance
  const ratio = (() => {
    if (calPoints.length === 2 && parseFloat(calLength) > 0) {
      const d = dist(calPoints[0], calPoints[1]);
      if (d > 0) return parseFloat(calLength) / d;
    }
    return null;
  })();

  const unitLabel = unit === 'feet' ? 'ft' : 'm';

  const realLen = (normLen) => (ratio ? normLen * ratio : null);
  const realArea = (normArea) => (ratio ? normArea * ratio * ratio : null);

  const liveArea = realArea(polygonArea(points));
  const livePerimeter = realLen(polygonPerimeter(points, points.length >= 3));

  // Calibration confidence hint (report-only heuristic — does NOT affect any math).
  // A longer on-screen reference line means a smaller relative pixel error, so the
  // pixel->real ratio is more trustworthy. More marked corners also helps.
  const calConfidence = (() => {
    if (calPoints.length < 2 || !(parseFloat(calLength) > 0)) return null;
    const refDist = dist(calPoints[0], calPoints[1]); // normalized 0-1
    let level, score, tone;
    if (refDist >= 0.45) { level = 'High'; score = 90; tone = 'green'; }
    else if (refDist >= 0.25) { level = 'Medium'; score = 70; tone = 'amber'; }
    else { level = 'Low'; score = 45; tone = 'red'; }
    if (points.length >= 4 && level === 'Medium') { score += 5; }
    return { level, score, tone, refDist };
  })();

  // ---- Draw overlay ----
  const drawOverlay = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (canvas.width !== Math.round(rect.width) || canvas.height !== Math.round(rect.height)) {
      canvas.width = Math.round(rect.width);
      canvas.height = Math.round(rect.height);
    }
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const toPx = (p) => ({ x: p.x * W, y: p.y * H });

    // ----- Polygon edges -----
    if (points.length > 0) {
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#22c55e';
      ctx.fillStyle = 'rgba(34,197,94,0.18)';

      // filled area when closed
      if (points.length >= 3) {
        ctx.beginPath();
        points.forEach((p, i) => {
          const px = toPx(p);
          if (i === 0) ctx.moveTo(px.x, px.y);
          else ctx.lineTo(px.x, px.y);
        });
        ctx.closePath();
        ctx.fill();
      }

      // edges + edge length labels
      for (let i = 0; i < points.length; i++) {
        const a = toPx(points[i]);
        const nextIndex = i + 1;
        let b = null;
        if (nextIndex < points.length) b = toPx(points[nextIndex]);
        else if (points.length >= 3) b = toPx(points[0]); // closing edge
        if (b) {
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
          // edge length label
          const nl = dist(points[i], nextIndex < points.length ? points[nextIndex] : points[0]);
          const rl = realLen(nl);
          const label = rl != null ? `${rl.toFixed(2)} ${unitLabel}` : 'calibrate';
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          ctx.font = 'bold 13px sans-serif';
          const tw = ctx.measureText(label).width;
          ctx.fillStyle = 'rgba(15,23,42,0.85)';
          ctx.fillRect(mx - tw / 2 - 5, my - 10, tw + 10, 20);
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, mx, my);
        }
      }

      // vertices
      points.forEach((p, i) => {
        const px = toPx(p);
        ctx.beginPath();
        ctx.arc(px.x, px.y, 7, 0, Math.PI * 2);
        ctx.fillStyle = i === 0 ? '#16a34a' : '#22c55e';
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();
      });
    }

    // ----- Calibration line -----
    if (calPoints.length > 0) {
      ctx.strokeStyle = '#f59e0b';
      ctx.fillStyle = '#f59e0b';
      ctx.lineWidth = 3;
      if (calPoints.length === 2) {
        const a = toPx(calPoints[0]);
        const b = toPx(calPoints[1]);
        ctx.beginPath();
        ctx.setLineDash([8, 6]);
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      calPoints.forEach((p) => {
        const px = toPx(p);
        ctx.beginPath();
        ctx.arc(px.x, px.y, 7, 0, Math.PI * 2);
        ctx.fillStyle = '#f59e0b';
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();
      });
    }
  }, [points, calPoints, ratio, unitLabel]);

  useEffect(() => {
    drawOverlay();
  }, [drawOverlay, cameraReady]);

  useEffect(() => {
    const onResize = () => drawOverlay();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [drawOverlay]);

  // ---- Tap handler ----
  const handleTap = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX;
    const clientY = e.clientY;
    let x = (clientX - rect.left) / rect.width;
    let y = (clientY - rect.top) / rect.height;
    x = Math.min(1, Math.max(0, x));
    y = Math.min(1, Math.max(0, y));
    const pt = { x, y };

    if (phase === 'calibrate') {
      setCalPoints((prev) => (prev.length >= 2 ? [pt] : [...prev, pt]));
    } else {
      setPoints((prev) => [...prev, pt]);
      setRedoStack([]);
    }
  };

  // ---- Controls ----
  const undo = () => {
    if (phase === 'calibrate') {
      setCalPoints((prev) => prev.slice(0, -1));
      return;
    }
    setPoints((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setRedoStack((r) => [...r, last]);
      return prev.slice(0, -1);
    });
  };

  const redo = () => {
    if (phase === 'calibrate') return;
    setRedoStack((r) => {
      if (r.length === 0) return r;
      const item = r[r.length - 1];
      setPoints((p) => [...p, item]);
      return r.slice(0, -1);
    });
  };

  const reset = () => {
    if (phase === 'calibrate') {
      setCalPoints([]);
    } else {
      setPoints([]);
      setRedoStack([]);
    }
  };

  const captureThumbnail = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      toast.error('Camera not ready for capture');
      return;
    }
    const c = document.createElement('canvas');
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    c.getContext('2d').drawImage(video, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL('image/jpeg', 0.7);
    setThumbnail(dataUrl);
    toast.success('Room photo captured for report');
  };

  const currentRoomValid = points.length >= 3 && ratio != null;

  // Build the payload for the current in-progress room (or null if invalid).
  const buildCurrentRoomPayload = (index) => {
    if (points.length < 3 || ratio == null) return null;
    const normArea = polygonArea(points);
    const normPerim = polygonPerimeter(points, true);
    return {
      polygonPoints: points.map((p) => ({ x: p.x, y: p.y })),
      referenceLinePixels: {
        x1: calPoints[0].x, y1: calPoints[0].y,
        x2: calPoints[1].x, y2: calPoints[1].y
      },
      referenceLength: parseFloat(calLength),
      unit,
      roomName: (roomName || '').trim() || `Room ${index}`,
      captureMeta: {
        device: 'mobile',
        markedPoints: points.length,
        calibration: { referenceLength: parseFloat(calLength), unit, points: calPoints }
      },
      thumbnailData: thumbnail,
      // client-side computed values for combined total display
      _area: realArea(normArea),
      _perimeter: realLen(normPerim)
    };
  };

  const resetForNextRoom = () => {
    setPoints([]);
    setRedoStack([]);
    setCalPoints([]);
    setCalLength('');
    setRoomName('');
    setThumbnail(null);
    setPhase('mark');
  };

  const addRoomAndContinue = () => {
    if (!currentRoomValid) {
      toast.error('Mark at least 3 corners and calibrate before adding the room');
      return;
    }
    const payload = buildCurrentRoomPayload(savedRooms.length + 1);
    setSavedRooms((prev) => [...prev, payload]);
    toast.success(`Saved "${payload.roomName}". Start the next room.`);
    resetForNextRoom();
  };

  const combinedArea = (() => {
    let total = savedRooms.reduce((s, r) => s + (r._area || 0), 0);
    if (currentRoomValid) total += realArea(polygonArea(points)) || 0;
    return total;
  })();

  const totalRoomCount = savedRooms.length + (currentRoomValid ? 1 : 0);
  const canFinish = totalRoomCount >= 1;

  const handleFinish = () => {
    const rooms = [...savedRooms];
    const current = buildCurrentRoomPayload(rooms.length + 1);
    if (current) rooms.push(current);
    if (rooms.length === 0) {
      toast.error('Add at least one room (3+ corners + calibration)');
      if (ratio == null) setPhase('calibrate');
      return;
    }
    setSubmitting(true);
    // strip client-only fields
    const cleanRooms = rooms.map(({ _area, _perimeter, ...r }) => r);
    stopCamera();
    onComplete({ rooms: cleanRooms, unit });
  };

  const handleCancel = () => {
    stopCamera();
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="live-measure-modal">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
        <div className="flex items-center gap-2">
          <Ruler className="w-5 h-5 text-green-400" />
          <span className="font-semibold">Live Camera Measurement</span>
        </div>
        <button
          onClick={handleCancel}
          className="p-2 rounded-lg hover:bg-slate-700"
          data-testid="live-measure-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Camera / overlay area */}
      <div ref={containerRef} className="relative flex-1 overflow-hidden bg-black">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 w-full h-full object-cover"
        />
        <canvas
          ref={canvasRef}
          onClick={handleTap}
          className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
          data-testid="live-measure-canvas"
        />

        {/* Camera error */}
        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-slate-900/90 text-white">
            <Camera className="w-10 h-10 mb-3 text-red-400" />
            <p className="mb-4">{cameraError}</p>
            <button onClick={startCamera} className="px-4 py-2 bg-green-600 rounded-lg font-medium">
              Retry Camera
            </button>
          </div>
        )}

        {/* Phase + running stats HUD */}
        {!cameraError && (
          <>
            <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-2 items-center">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${phase === 'mark' ? 'bg-green-500 text-white' : 'bg-white/20 text-white'}`}>
                {phase === 'mark' ? 'Marking corners' : 'Calibrating'}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-black/50 text-white">
                Points: {points.length}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${ratio ? 'bg-green-600 text-white' : 'bg-amber-500 text-white'}`}>
                {ratio ? 'Calibrated' : 'Not calibrated'}
              </span>
              {calConfidence && (
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${calConfidence.tone === 'green' ? 'bg-emerald-600' : calConfidence.tone === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`}
                  data-testid="calibration-confidence-chip"
                >
                  {calConfidence.level} confidence · ~{calConfidence.score}%
                </span>
              )}
            </div>

            <div className="absolute bottom-3 left-3 right-3 bg-black/60 text-white rounded-lg px-4 py-2 flex justify-around text-center">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-300">Perimeter</p>
                <p className="text-sm font-bold" data-testid="live-perimeter">
                  {livePerimeter != null ? `${livePerimeter.toFixed(2)} ${unitLabel}` : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-300">Area</p>
                <p className="text-sm font-bold" data-testid="live-area">
                  {liveArea != null ? `${liveArea.toFixed(2)} ${unitLabel}²` : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-300">Rooms: {savedRooms.length}</p>
                <p className="text-sm font-bold text-green-400" data-testid="live-combined-area">
                  Total {combinedArea.toFixed(2)} {unitLabel}²
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Controls */}
      <div className="bg-slate-900 text-white px-4 py-3 space-y-3">
        {/* Instructions */}
        <p className="text-xs text-slate-300 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5 text-green-400" />
          {phase === 'mark'
            ? 'Tap each wall corner in order around the room.'
            : 'Tap two ends of a known length (e.g. a door), then enter its real size below.'}
        </p>

        {/* Room name */}
        <input
          type="text"
          value={roomName}
          onChange={(e) => setRoomName(e.target.value)}
          placeholder={`Room name (e.g. Kitchen) — defaults to "Room ${savedRooms.length + 1}"`}
          className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
          data-testid="room-name-input"
        />

        {/* Phase toggle */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setPhase('mark')}
            className={`py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1 ${phase === 'mark' ? 'bg-green-600' : 'bg-slate-700'}`}
            data-testid="phase-mark"
          >
            <Crosshair className="w-4 h-4" /> Mark Corners
          </button>
          <button
            onClick={() => setPhase('calibrate')}
            className={`py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1 ${phase === 'calibrate' ? 'bg-amber-500' : 'bg-slate-700'}`}
            data-testid="phase-calibrate"
          >
            <Ruler className="w-4 h-4" /> Calibrate
          </button>
        </div>

        {/* Calibration inputs */}
        {phase === 'calibrate' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={calLength}
                onChange={(e) => setCalLength(e.target.value)}
                placeholder={`Known length (${unitLabel})`}
                className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
                data-testid="cal-length-input"
              />
              <span className="text-xs text-slate-300">{calPoints.length}/2 pts</span>
            </div>
            {calConfidence ? (
              <div
                className={`rounded-lg px-3 py-2 text-xs ${calConfidence.tone === 'green' ? 'bg-emerald-900/50 text-emerald-200' : calConfidence.tone === 'amber' ? 'bg-amber-900/40 text-amber-200' : 'bg-red-900/40 text-red-200'}`}
                data-testid="calibration-confidence-hint"
              >
                <div className="flex items-center justify-between font-semibold">
                  <span>Estimated accuracy: {calConfidence.level} (~{calConfidence.score}%)</span>
                  <span>{Math.round(calConfidence.refDist * 100)}% of frame</span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-black/30 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${calConfidence.tone === 'green' ? 'bg-emerald-400' : calConfidence.tone === 'amber' ? 'bg-amber-400' : 'bg-red-400'}`}
                    style={{ width: `${calConfidence.score}%` }}
                  />
                </div>
                {calConfidence.level !== 'High' && (
                  <p className="mt-1 opacity-90">
                    Tip: calibrate against the longest known length in view (a full door or wall) for a more reliable area.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-slate-400">
                Mark two ends of a known length, then enter its real size to see an accuracy estimate.
              </p>
            )}
          </div>
        )}

        {/* Unit + undo/redo/reset */}
        <div className="flex items-center gap-2">
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={savedRooms.length > 0}
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm disabled:opacity-50"
            data-testid="unit-select"
          >
            <option value="meters">meters</option>
            <option value="feet">feet</option>
          </select>
          <button onClick={undo} className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 flex items-center justify-center gap-1 text-sm" data-testid="undo-btn">
            <Undo2 className="w-4 h-4" /> Undo
          </button>
          <button onClick={redo} className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 flex items-center justify-center gap-1 text-sm" data-testid="redo-btn">
            <Redo2 className="w-4 h-4" /> Redo
          </button>
          <button onClick={reset} className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 flex items-center justify-center gap-1 text-sm" data-testid="reset-btn">
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
        </div>

        {/* Thumbnail + Add Room + Finish */}
        <div className="flex items-center gap-2">
          <button
            onClick={captureThumbnail}
            className="py-2 px-3 rounded-lg bg-slate-700 hover:bg-slate-600 flex items-center justify-center gap-1 text-sm"
            data-testid="thumbnail-btn"
          >
            <ImageIcon className="w-4 h-4" /> {thumbnail ? 'Photo ✓' : 'Photo'}
          </button>
          <button
            onClick={addRoomAndContinue}
            disabled={!currentRoomValid || submitting}
            className={`py-2 px-3 rounded-lg flex items-center justify-center gap-1 text-sm font-medium ${currentRoomValid && !submitting ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' : 'bg-slate-600 opacity-60 cursor-not-allowed'}`}
            data-testid="add-room-btn"
          >
            <Plus className="w-4 h-4" /> Add Room
          </button>
          <button
            onClick={handleFinish}
            disabled={!canFinish || submitting}
            className={`flex-1 py-2 rounded-lg font-semibold flex items-center justify-center gap-1 text-sm ${canFinish && !submitting ? 'bg-green-600 hover:bg-green-500' : 'bg-slate-600 opacity-60 cursor-not-allowed'}`}
            data-testid="finish-btn"
          >
            <Check className="w-4 h-4" /> {submitting ? 'Calculating…' : `Finish (${totalRoomCount})`}
          </button>
        </div>

        {/* Caveat */}
        <p className="text-[11px] text-amber-300 flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5" />
          Estimated from live camera capture — verify critical dimensions with manual tools.
        </p>
      </div>
    </div>
  );
};

export default LiveMeasurementCapture;
